"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="14722" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in duration-500">
        <div className="rounded-full bg-primary/10 p-4">
          <Send className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-sans font-bold tracking-tight text-foreground">
            Transmission Received
          </h3>
          <p className="text-muted-foreground max-w-xs mx-auto font-sans">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 text-primary hover:bg-primary/10">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-mono text-xs uppercase tracking-wider">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Engineer Name" className="bg-background/50 border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-mono text-xs uppercase tracking-wider">Corporate Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="corporate@email.com" className="bg-background/50 border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-mono text-xs uppercase tracking-wider">Phone Number <span className="text-muted-foreground text-[10px] font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-background/50 border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-mono text-xs uppercase tracking-wider">Subject <span className="text-muted-foreground text-[10px] font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input placeholder="e.g., Optical Fiber RFQ" className="bg-background/50 border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-mono text-xs uppercase tracking-wider">Technical Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your technical requirements or project scope..." className="min-h-[150px] resize-none bg-background/50 border-border focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[160px] bg-primary text-primary-foreground hover:bg-primary/90 transition-all hover:shadow-[0_0_20px_rgba(0,255,255,0.3)]" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Transmitting...
          </> : "Initialize Inquiry"}
      </Button>
    </Form>;
}
export default function ConnectWithPrimeHold() {
  return <section id="contact-nexel" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-50" />
      <div className="absolute -left-64 top-0 size-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none opacity-40" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {}
          <div className="lg:col-span-5 space-y-10 pt-4">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-sans font-bold tracking-tighter text-foreground leading-tight">
                Contact Nexel Technologies
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                Partner with Nexel for industry-leading optical fiber solutions and advanced FPV drone systems. Our engineering team is ready to support your infrastructure needs globally.
              </p>
            </div>

            <div className="space-y-8">
              {}
              <div className="flex items-start gap-4 group">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-card/50 text-primary shadow-sm transition-all group-hover:border-primary/50 group-hover:bg-primary/10 group-hover:shadow-[0_0_15px_rgba(0,255,255,0.2)]">
                  <MapPin className="size-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-bold text-foreground font-sans uppercase tracking-wider text-sm">Global Headquarters</h3>
                  <p className="text-muted-foreground font-mono text-sm">Nanshan, Shenzhen, China | Global Distribution Centers</p>
                </div>
              </div>

              {}
              <div className="flex items-start gap-4 group">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-card/50 text-primary shadow-sm transition-all group-hover:border-primary/50 group-hover:bg-primary/10 group-hover:shadow-[0_0_15px_rgba(0,255,255,0.2)]">
                  <Mail className="size-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-bold text-foreground font-sans uppercase tracking-wider text-sm">Technical Support</h3>
                  <p className="text-muted-foreground font-mono text-sm">support@galabit.com</p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border/50">
              <p className="text-xs text-muted-foreground font-mono leading-relaxed uppercase tracking-widest opacity-70">
                For technical specifications regarding G652D/G657 fibers or drone system integration, please specify your requirements for direct routing to our engineering department.
              </p>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="border-none shadow-2xl bg-card/40 backdrop-blur-xl ring-1 ring-white/10">
              <CardContent className="p-8 md:p-10">
                <div className="mb-8">
                  <h3 className="text-2xl font-sans font-bold tracking-tight text-foreground mb-2">
                    Technical Inquiry Portal
                  </h3>
                  <p className="text-muted-foreground font-sans">
                    Submit your project requirements or RFQ details below and our team will respond within 24 hours.
                  </p>
                </div>

                <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="contact-nexel" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                subject: "",
                message: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Initialize Inquiry" successMessage="Thank you for your inquiry. Our technical engineering team will review your requirements and contact you within 24 hours.">
                  <ContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}